/**
 * Data layer for `anomaly-alerts-ml-driven-unusual-activity-patterns`.
 * Thin typed fetch wrappers over /api/v1/anomaly-alerts-ml-driven-unusual-activity-patterns.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type AnomalySeverity = "low" | "medium" | "high" | "critical";
export type AnomalyStatus = "open" | "acknowledged";

export interface AnomalyAlertRow {
	id: number;
	tenant_id: string;
	alert_id: string;
	pattern_type: string;
	anomaly_score: number;
	entity_type: string;
	entity_id: string;
	signals_json: string;
	severity: AnomalySeverity;
	status: AnomalyStatus;
	payload_json: string;
	detected_at: number;
	acknowledged_at: number | null;
	created_at: number;
}

export interface ListAnomalyAlertsResponse {
	items: AnomalyAlertRow[];
}

export interface ListAnomalyAlertsQuery {
	status?: AnomalyStatus | "";
	severity?: AnomalySeverity | "";
	min_score?: number;
}

export interface AcknowledgeResponse {
	ok: true;
	alert_id: string;
	status: "acknowledged";
	acknowledged_at: number;
}

const BASE = "/api/v1/anomaly-alerts-ml-driven-unusual-activity-patterns";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	q: ListAnomalyAlertsQuery = {},
): Promise<ListAnomalyAlertsResponse> {
	const p = new URLSearchParams();
	if (q.status) p.set("status", q.status);
	if (q.severity) p.set("severity", q.severity);
	if (q.min_score !== undefined) p.set("min_score", String(q.min_score));
	const qs = p.toString();
	const url = qs ? `${BASE}?${qs}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAnomalyAlertsResponse>(res);
}

export async function acknowledge(alertId: string): Promise<AcknowledgeResponse> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(alertId)}/acknowledge`,
		{
			method: "POST",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify({}),
		},
	);
	return parseOrThrow<AcknowledgeResponse>(res);
}

export const anomalyAlertsQueryKeys = {
	all: ["anomaly-alerts-ml-driven-unusual-activity-patterns"] as const,
	list: (q: ListAnomalyAlertsQuery = {}) =>
		[
			...anomalyAlertsQueryKeys.all,
			"list",
			q.status ?? "",
			q.severity ?? "",
			q.min_score ?? "",
		] as const,
	detail: (id: string) =>
		[...anomalyAlertsQueryKeys.all, id] as const,
};
